/**
 * TimelineRowHelpers Component
 *
 * Static overlay labels for dual-layer timeline in crop modal.
 * Renders "Crop Phases" and "Crop Tasks" labels aligned with each row.
 */

import { Chip, useTheme } from '@mui/material'
import { EXPANDED_ROW_HEIGHT, EXPANDED_ROW_HEIGHT_GAP, X_AXIS_HEIGHT } from '../timeline-constants'

// Hover line offset (matches timeline-hover-line.jsx top: 16)
const HOVER_LINE_OFFSET = 16

/**
 * TimelineRowHelpers - Row helper overlay labels
 *
 * Displays static "Crop Phases" and "Crop Tasks" labels positioned to align
 * with the dual-layer timeline rows (viewStrategy='both').
 *
 * Design:
 * - Absolutely positioned overlays (don't scroll with content)
 * - Non-interactive (pointerEvents: 'none')
 * - Simplified styling (no hover effects or buttons)
 * - Only renders in crop modal (via children prop to CropTimeline)
 */
export const TimelineRowHelpers = () => {
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'

  // Calculate vertical positions to align with phase/task rows
  // Top helper: aligns with center of top half of dual-layer row (phases)
  // Bottom helper: aligns with center of bottom half of dual-layer row (tasks)
  // Note: HOVER_LINE_OFFSET accounts for the space reserved for the hover line date label
  const topHelperY = X_AXIS_HEIGHT + HOVER_LINE_OFFSET + (EXPANDED_ROW_HEIGHT / 2) + EXPANDED_ROW_HEIGHT_GAP / 2
  const bottomHelperY = topHelperY + EXPANDED_ROW_HEIGHT + EXPANDED_ROW_HEIGHT_GAP / 4

  // Shared chip styles (simplified from CropScheduleLabelChip)
  const chipStyle = {
    position: 'absolute',
    left: '0.5rem',
    height: EXPANDED_ROW_HEIGHT - 2,
    backgroundColor: isDark ? theme.palette.grey[700] : theme.palette.grey[200],
    color: isDark ? theme.palette.common.white : theme.palette.text.secondary,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    pointerEvents: 'none',  // Non-interactive
    zIndex: 35,  // Above Y-axis (z-index: 30), below gradient mask
    transform: 'translateY(-50%)',  // Center chip vertically
  }

  return (
    <>
      {/* Crop Phases helper */}
      <Chip
        label="Crop Phases"
        variant="outlined"
        size="small"
        sx={{
          ...chipStyle,
          top: topHelperY,
          px: 0.5,
        }}
      />

      {/* Crop Tasks helper */}
      <Chip
        label={
          <>
            Crop Task
            <br />
            Windows
          </>
        }
        variant="outlined"
        size="small"
        sx={{
          ...chipStyle,
          top: bottomHelperY,
          px: 0.5,
        }}
      />
    </>
  )
}
